import { supabase } from './supabase';
import { getFirstImageByProductIds } from './maison-adji-api';

/** Forme utilisée par le contexte / UI (identique à MaisonAdjiCartItem). */
export interface EnrichedCartItem {
  productId: string;
  slug: string;
  name: string;
  nameEn: string | null;
  priceXof: number | null;
  priceEur: number | null;
  quantity: number;
  imageUrl: string;
}

/** Récupère ou crée un panier pour le profil (utilisateur connecté). */
export async function getOrCreateCart(profileId: string): Promise<string | null> {
  const { data: existingList } = await supabase
    .from('carts')
    .select('id')
    .eq('profile_id', profileId)
    .order('created_at', { ascending: true })
    .limit(1);

  const existingId = existingList?.[0]?.id;
  if (existingId) return existingId;

  const { data: created, error } = await supabase
    .from('carts')
    .insert({ profile_id: profileId, currency: 'XOF' })
    .select('id')
    .single();

  if (error || !created?.id) return null;
  return created.id;
}

export interface SupabaseCartItemRow {
  id: string;
  cart_id: string;
  product_id: string;
  variant_id: string | null;
  quantity: number;
  unit_price: number;
  custom_note: string | null;
}

/** Charge le panier et ses lignes pour un profil. */
export async function getCartWithItems(profileId: string): Promise<{
  cartId: string | null;
  items: SupabaseCartItemRow[];
}> {
  const cartId = await getOrCreateCart(profileId);
  if (!cartId) return { cartId: null, items: [] };

  const { data: rows, error } = await supabase
    .from('cart_items')
    .select('id, cart_id, product_id, variant_id, quantity, unit_price, custom_note')
    .eq('cart_id', cartId)
    .order('created_at');

  if (error) return { cartId, items: [] };
  return { cartId, items: (rows ?? []) as SupabaseCartItemRow[] };
}

/** Ajoute ou met à jour une ligne (même product_id = même ligne, on somme les quantités). */
export async function upsertCartItem(
  cartId: string,
  productId: string,
  quantity: number,
  unitPrice: number,
  variantId: string | null = null
): Promise<boolean> {
  const { data: existingList } = await supabase
    .from('cart_items')
    .select('id, quantity')
    .eq('cart_id', cartId)
    .eq('product_id', productId)
    .limit(1);

  const existing = existingList?.[0];
  if (existing) {
    const { error } = await supabase
      .from('cart_items')
      .update({ quantity: existing.quantity + quantity, updated_at: new Date().toISOString() })
      .eq('id', existing.id);
    return !error;
  }

  const { error } = await supabase.from('cart_items').insert({
    cart_id: cartId,
    product_id: productId,
    variant_id: variantId,
    quantity,
    unit_price: unitPrice,
  });
  return !error;
}

/** Supprime une ligne par product_id. */
export async function removeCartItemByProduct(
  cartId: string,
  productId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('cart_items')
    .delete()
    .eq('cart_id', cartId)
    .eq('product_id', productId);
  return !error;
}

/** Met à jour la quantité d'une ligne (par product_id). Consolide en une seule ligne si doublons. */
export async function updateCartItemQuantity(
  cartId: string,
  productId: string,
  quantity: number
): Promise<boolean> {
  if (quantity < 1) return removeCartItemByProduct(cartId, productId);

  const { data: rows } = await supabase
    .from('cart_items')
    .select('id, unit_price')
    .eq('cart_id', cartId)
    .eq('product_id', productId)
    .limit(10);

  if (!rows?.length) return false;
  const first = rows[0];
  if (rows.length === 1) {
    const { error } = await supabase
      .from('cart_items')
      .update({ quantity, updated_at: new Date().toISOString() })
      .eq('id', first.id);
    return !error;
  }
  await supabase.from('cart_items').delete().eq('cart_id', cartId).eq('product_id', productId);
  const { error } = await supabase.from('cart_items').insert({
    cart_id: cartId,
    product_id: productId,
    variant_id: null,
    quantity,
    unit_price: first.unit_price,
  });
  return !error;
}

/** Regroupe les lignes par product_id et somme les quantités (évite doublons d’affichage). */
function groupRowsByProduct(rows: SupabaseCartItemRow[]): SupabaseCartItemRow[] {
  const byProduct = new Map<string, SupabaseCartItemRow>();
  for (const row of rows) {
    const existing = byProduct.get(row.product_id);
    if (existing) {
      existing.quantity += row.quantity;
    } else {
      byProduct.set(row.product_id, { ...row });
    }
  }
  return Array.from(byProduct.values());
}

/** Charge le panier enrichi (nom, slug, prix, image) pour l'affichage. */
export async function getEnrichedCartItems(profileId: string): Promise<EnrichedCartItem[]> {
  const { cartId, items } = await getCartWithItems(profileId);
  if (!cartId || items.length === 0) return [];

  const grouped = groupRowsByProduct(items);
  const productIds = [...new Set(grouped.map((i) => i.product_id))];
  const { data: products } = await supabase
    .from('products')
    .select('id, name, name_en, slug, price_xof, price_eur')
    .in('id', productIds);
  const imageByProduct = await getFirstImageByProductIds(productIds);

  const productMap = new Map(
    (products ?? []).map((p: any) => [
      p.id,
      {
        name: p.name,
        name_en: p.name_en ?? null,
        slug: p.slug,
        price_xof: p.price_xof != null ? Number(p.price_xof) : null,
        price_eur: p.price_eur != null ? Number(p.price_eur) : null,
      },
    ])
  );

  return grouped.map((row) => {
    const p = productMap.get(row.product_id);
    return {
      productId: row.product_id,
      slug: p?.slug ?? '',
      name: p?.name ?? '',
      nameEn: p?.name_en ?? null,
      priceXof: p?.price_xof ?? null,
      priceEur: p?.price_eur ?? null,
      quantity: row.quantity,
      imageUrl: imageByProduct[row.product_id] ?? '',
    };
  });
}

/** Fusionne les lignes du panier local dans le panier Supabase du profil. */
export async function mergeLocalCartToSupabase(
  profileId: string,
  localItems: { productId: string; quantity: number; priceXof: number | null; priceEur: number | null }[]
): Promise<boolean> {
  if (localItems.length === 0) return true;
  const cartId = await getOrCreateCart(profileId);
  if (!cartId) return false;

  for (const item of localItems) {
    const unitPrice = item.priceXof ?? item.priceEur ?? 0;
    await upsertCartItem(cartId, item.productId, item.quantity, unitPrice, null);
  }
  return true;
}
